import type { ElementType, ReactElement } from 'react';
import {
  Box,
  Flex,
  Icon,
  Popover,
  PopoverAnchor,
  PopoverBody,
  PopoverContent,
  type PopoverProps,
} from '@chakra-ui/react';
import { XClose } from '@kula-ai/icons-core';
import { KText } from '@/ui-library/components/text';

type TailPlacement = 'left' | 'bottom';

interface KEducationalTooltipProps {
  anchor: ReactElement;
  icon: ElementType;
  title: string;
  description: string;
  isOpen: boolean;
  onDismiss: () => void;
  placement?: PopoverProps['placement'];
  width?: string;
  gutter?: number;
  tailPlacement?: TailPlacement;
  tailOffset?: string;
  closeTestId?: string;
}

export const KEducationalTooltip = ({
  anchor,
  icon,
  title,
  description,
  isOpen,
  onDismiss,
  placement = 'right',
  width = '400px',
  gutter = 12,
  tailPlacement = 'left',
  tailOffset = '50%',
  closeTestId,
}: KEducationalTooltipProps) => {
  const tailStyles =
    tailPlacement === 'bottom'
      ? {
          bottom: '-6px',
          right: tailOffset,
          borderLeft: '1px solid',
          borderBottom: '1px solid',
          transform: 'rotate(-45deg)',
        }
      : {
          left: '-6px',
          top: tailOffset,
          borderLeft: '1px solid',
          borderBottom: '1px solid',
          transform: 'translateY(-50%) rotate(45deg)',
        };

  return (
    <Popover
      isOpen={isOpen}
      placement={placement}
      closeOnBlur={false}
      autoFocus={false}
      returnFocusOnClose={false}
      gutter={gutter}
      modifiers={[{ name: 'preventOverflow', options: { padding: 12 } }]}
    >
      <PopoverAnchor>{anchor}</PopoverAnchor>
      <PopoverContent
        width={width}
        rootProps={{ zIndex: 1 }}
        bg="primary-25"
        border="1px solid"
        borderColor="primary-400"
        borderRadius="6px"
        boxShadow="0px 12px 32px rgba(38, 34, 68, 0.12)"
        _focus={{
          outline: 'none',
          boxShadow: '0px 12px 32px rgba(38, 34, 68, 0.12)',
        }}
        _focusVisible={{ outline: 'none' }}
        _before={{
          content: '""',
          position: 'absolute',
          width: '12px',
          height: '12px',
          bg: 'primary-25',
          borderColor: 'primary-400',
          ...tailStyles,
        }}
      >
        <PopoverBody p="12px">
          <Flex justifyContent="space-between" alignItems="flex-start" mb="8px">
            <Flex alignItems="center" gap="8px">
              <Icon as={icon} boxSize="18px" color="secondary-700" />
              <KText
                fontSize="14px"
                fontWeight="medium"
                color="secondary-900"
                lineHeight="20px"
              >
                {title}
              </KText>
            </Flex>
            <Box
              as="button"
              type="button"
              display="flex"
              alignItems="center"
              justifyContent="center"
              p="0"
              bg="transparent"
              border="0"
              cursor="pointer"
              aria-label={`Dismiss ${title}`}
              onClick={onDismiss}
              data-test-id={closeTestId}
            >
              <Icon
                as={XClose}
                boxSize="16px"
                color="secondary-900"
                flexShrink={0}
              />
            </Box>
          </Flex>
          <KText
            fontSize="13px"
            fontWeight="regular"
            color="secondary-700"
            lineHeight="20px"
          >
            {description}
          </KText>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};
